import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-phnom-kulen-srah-damrei.jpg";

const itinerary = [
  {
    time: "Day 1 · 8:00 AM",
    title: "Hotel Pick-up & Drive to Phnom Kulen",
    body:
      "Pick up from your hotel in Siem Reap and a 1.5–2 hour scenic countryside drive to the entrance of Phnom Kulen National Park.",
  },
  {
    time: "Day 1 · Morning",
    title: "Srah Damrei Temple Stone of the Sacred Elephant",
    body:
      "Visit the remote Srah Damrei site, exploring jungle-bound carvings of stone elephants and lions and learning about early Khmer spiritual beliefs.",
  },
  {
    time: "Day 1 · Midday",
    title: "River of 1000 Lingas",
    body:
      "Walk the sacred riverbed carved with hundreds of lingas, yonis and Hindu symbols, with short jungle walks along the holy stream.",
  },
  {
    time: "Day 1 · Afternoon",
    title: "Kulen Waterfall Swim & Picnic",
    body:
      "Swim and relax at the famous Kulen Waterfall, with a picnic lunch or local restaurant nearby and free time for photography and nature.",
  },
  {
    time: "Day 1 · Sunset",
    title: "Bat Cave Experience",
    body:
      "Travel to the nearby bat cave viewpoint and watch thousands of bats stream out at sunset a magical end to the day.",
  },
  {
    time: "Day 1 · Evening",
    title: "Eco-lodge Check-in & Dinner",
    body:
      "Check in at an eco-lodge or guesthouse near Phnom Kulen, share a quiet dinner, and unwind in the peaceful mountain environment.",
  },
  {
    time: "Day 2 · Sunrise",
    title: "Sunrise & Nature Walk",
    body:
      "Wake for sunrise in the jungle and take a light walk along nature trails or through the local mountain village.",
  },
  {
    time: "Day 2 · Morning",
    title: "Breakfast & Optional Revisit",
    body:
      "Breakfast at the lodge, optional revisit to the waterfall or temple areas, and time to learn about local mountain community life.",
  },
  {
    time: "Day 2 · 12:00–1:00 PM",
    title: "Drive Back to Siem Reap",
    body:
      "Late-morning departure with private transfer back to Siem Reap and drop-off at your hotel.",
  },
];

const includes = [
  "English-speaking driver",
  "Private air-conditioned transport",
  "1-night accommodation (eco-lodge or guesthouse)",
  "1 dinner, 1 breakfast & 1 lunch",
  "Drinking water",
];

const excludes = [
  "Phnom Kulen entrance fee ($20 per person)",
  "Lunch on Day 1",
  "Guide service (optional +$50/day)",
  "Personal expenses & drinks",
];

const essentials = [
  "Hat, sunglasses & sunscreen",
  "Insect repellent",
  "Binoculars",
  "Swimwear & quick-dry clothing",
];

const pricing = [
  { group: "1–2 people", price: "$350 – $395" },
  { group: "3–5 people", price: "$445 – $555" },
  { group: "6–10 people", price: "$599 – $798" },
];

const TourPhnomKulenSrahDamrei = () => {
  return (
    <main className="bg-background">
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Phnom Kulen Srah Damrei elephant pond private jungle tour with French-speaking guide, Cambodia"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 2 Days / 1 Night Sacred Mountain Adventure</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Phnom Kulen <em className="font-light">Srah Damrei, Waterfall & Bat Cave</em>.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              Cambodia's holy mountain ancient stone elephants in the jungle, the River of 1000 Lingas, a swim at Kulen Waterfall, and thousands of bats streaming out at sunset.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 2 Days / 1 Night</span>
              <span>Group · 1–10</span>
              <span>Pickup · 8:00 AM</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            The sacred mountain, slowly.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Discover the sacred mountain of Phnom Kulen National Park on an immersive 2-day adventure. Visit the ancient Srah Damrei temple site, swim at Kulen Waterfall, explore the mystical River of 1000 Lingas, and enjoy a sunset bat cave experience before staying overnight in nature.
            </p>
            <p>
              Day 1 takes you deep into the jungle to the stone elephants of Srah Damrei, along the carved sacred riverbed of the 1000 Lingas, and to the cooling pools of Kulen Waterfall closing with a sunset bat cave spectacle and a quiet dinner at your eco-lodge.
            </p>
            <p>
              Day 2 begins with sunrise in the jungle, an optional revisit to favourite sites and time among the mountain community, before a relaxed late-morning drive back to Siem Reap.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                Two days, one night, nine stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar
              pricePerPerson={350}
              tourTitle="2 Days / 1 Night Phnom Kulen — Srah Damrei, Waterfall, 1000 Lingas & Bat Cave"
              pricingTiers={[
                { maxPax: 1, price: 350 },
                { maxPax: 2, price: 395 },
                { maxPax: 3, price: 445 },
                { maxPax: 4, price: 496 },
                { maxPax: 5, price: 555 },
                { maxPax: 6, price: 599 },
                { maxPax: 7, price: 656 },
                { maxPax: 8, price: 696 },
                { maxPax: 9, price: 745 },
                { maxPax: 10, price: 798 },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Pricing</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Private tour price (per group).
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((p) => (
              <div key={p.group} className="bg-background p-8 border border-border">
                <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">{p.group}</p>
                <p className="font-display text-3xl">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need on the mountain.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Physical Effort</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                No long uphill hikes required, but you should be fit enough to walk up to 1 km in hot conditions, sometimes on uneven jungle ground. Within the capability of most healthy travellers comfortable with tropical climates.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Hot and dry weather is expected throughout. Occasional showers are possible. Midday is hot, but the woodland sites have lower humidity. The pace is designed so you avoid touring through the worst of the heat.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Khmer cuisine is similar to most Southeast Asian cooking but rather less spicy. Lodges prepare a wide variety of good local food, with fresh bread widely available and a surprising range of local brews to try.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Bring With You</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourPhnomKulenSrahDamrei;
